import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";

/**
 * Reusable Work Order Search Component
 * @param {Object} props - Component props
 * @param {Function} props.onSelectWorkOrder - Callback when a work order is selected
 * @param {Array} props.displayColumns - Columns to display ['woId', 'jobName', 'customerName', 'WOStatus']
 * @param {string} props.activeTab - Tab to filter work orders by (default: "all")
 * @param {string} props.title - Optional title to display above the search component
 * @param {string} props.selectButtonLabel - Label for the select button (default: "Select")
 * @param {boolean} props.includeProducts - Whether to include products in the search results
 * @param {boolean} props.includeLocation - Whether to include location in the search results
 * @param {Function} props.customSearchFunction - Optional custom search function that receives search parameters
 * (page, limit, query, includeProducts, includeLocation, activeTab) and should return an object with
 * {workOrders: Array, pages: number, total?: number} structure
 */
export default function SearchWorkOrders({
  onSelectWorkOrder,
  displayColumns = ["woId", "jobName", "customerName", "WOStatus"],
  activeTab = "all",
  title = "",
  selectButtonLabel = "Select",
  includeProducts = false,
  includeLocation = false,
  customSearchFunction = null,
  getAutoFirstPage = true,
}) {
  const dispatch = useDispatch();
  /*   All States
   ********************************************* */

  // States
  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  /*   All Functions
   ********************************************* */

  // Get work orders data
  const getWorkOrders = async () => {
    setIsLoading(true);
    try {
      // Use custom search function if provided, otherwise use default search
      if (customSearchFunction) {
        const searchParams = {
          page: currentPage,
          limit,
          query: searchQuery || null,
          includeProducts,
          includeLocation,
          activeTab,
          // Pass any other relevant props that might be needed by the custom search function
        };

        const result = await customSearchFunction(searchParams);

        if (result) {
          setWorkOrders(result.workOrders || []);
          setTotalPages(result.pages || 1);
          // Handle any additional data that might be returned
          if (result.total) setTotal(result.total);
        }
      } else {
        let body = {
          page: currentPage,
          limit: limit,
          query: searchQuery || null,
          searchFields: [], // Optional - specific fields to search in
          includeProducts: includeProducts, // Optional - include products in the response
          includeLocation: includeLocation, // Optional - include location in the response
          activeTab: activeTab, // Use the activeTab for filtering if needed
        };

        const { data } = await API.post("/work-orders-search", body, {
          headers: authHeader(),
        });

        setWorkOrders(data.workOrders || []);
        setTotalPages(data.pages || 1);
      }

      setIsLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
      setIsLoading(false);
    }
  };

  // Handle search submission with support for custom search function
  const handleSearch = (e) => {
    e.preventDefault();
    // Reset to page 1 when searching
    setCurrentPage(1);
    getWorkOrders();
  };

  // Converting SQL timestamp to readable date
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle work order selection
  const handleSelect = (workOrder) => {
    if (onSelectWorkOrder) {
      onSelectWorkOrder(workOrder);
    }
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li
        key="prev"
        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
      </li>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li
        key="next"
        className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
      >
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </li>
    );

    return (
      <nav aria-label="Work order pagination">
        <ul className="pagination justify-content-center mb-0">{pages}</ul>
      </nav>
    );
  };

  // Function to render a table cell based on column name
  const renderCell = (item, column) => {
    switch (column) {
      case "woId":
        return `W-${item.woId}`;
      case "jobName":
        return truncateString(item.jobName, 20);
      case "customerName":
        return truncateString(item.customerName, 20);
      case "WOStatus":
        return item.WOStatus;
      case "createdAt":
        return convertSqlTimestampToDate(item.createdAt);
      default:
        return item[column] ? truncateString(item[column].toString(), 20) : "";
    }
  };

  // Generate table headers based on displayColumns prop
  const renderTableHeaders = () => {
    return displayColumns.map((column) => {
      // Convert column names to display format (e.g., 'woId' to 'W-ID')
      let headerName = column;
      if (column === "woId") headerName = "W-ID";
      else if (column === "jobName") headerName = "Job Name";
      else if (column === "customerName") headerName = "Customer Name";
      else if (column === "WOStatus") headerName = "Status";
      else if (column === "createdAt") headerName = "Created Date";
      else {
        // Convert camelCase to Title Case
        headerName = column
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, function (str) {
            return str.toUpperCase();
          });
      }

      return (
        <th key={column} scope="col">
          {headerName}
        </th>
      );
    });
  };

  /*   All UseEffects
   ********************************************* */
  // Initial data load and when dependencies change
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (getAutoFirstPage) {
        getWorkOrders();
      }
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, getAutoFirstPage]);

  return (
    <div className="work-orders-search-container">
      {title && (
        <>
          <h3 className="">{title}</h3>
          <hr className="mb-3 mt-2" />
        </>
      )}

      <div className="mb-3 d-flex justify-content-between">
        {/* Search Form */}
        <form onSubmit={handleSearch} className="d-flex">
          <input
            type="text"
            className="form-control me-2"
            placeholder="Search work orders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>

        {/* Items per page selector */}
        <div className="d-flex align-items-center">
          {total > 0 && (
            <span className="me-3">
              <strong>Total:</strong> {total}
            </span>
          )}
          <span className="me-2">Show:</span>
          <select
            className="form-select"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-4">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  {renderTableHeaders()}
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.length > 0 ? (
                  workOrders.map((item) => (
                    <tr key={item.id}>
                      {displayColumns.map((column) => (
                        <td
                          key={`${item.id}-${column}`}
                          className="my-0 py-1 align-middle"
                        >
                          {renderCell(item, column)}
                        </td>
                      ))}
                      <td className="my-0 py-1 align-middle">
                        <button
                          onClick={() => handleSelect(item)}
                          className="btn btn-sm btn-success"
                        >
                          {selectButtonLabel}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={displayColumns.length + 1}
                      className="text-center py-3"
                    >
                      {!getAutoFirstPage
                        ? "Enter W-ID in search..."
                        : "No work orders found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && <Pagination />}
        </>
      )}
    </div>
  );
}
